import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Plus, Trash2, Search, Upload, X } from 'lucide-react';

export function SupplierDocuments({ form, setForm, isEdit }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newDoc, setNewDoc] = useState({ description: '', start_date: '', end_date: '', attach_in_order: false });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    const doc = {
      file_name: file.name,
      description: newDoc.description,
      file_type: 'File',
      start_date: newDoc.start_date,
      end_date: newDoc.end_date,
      attach_in_order: newDoc.attach_in_order,
    };

    if (isEdit && form.supplier_code) {
      // Upload to backend
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', newDoc.description);
      fd.append('start_date', newDoc.start_date);
      fd.append('end_date', newDoc.end_date);
      fd.append('attach_in_order', newDoc.attach_in_order);
      try {
        await api.post(`/api/data/suppliers/${form.supplier_code}/documents`, fd);
        toast.success('Document uploaded');
      } catch { toast.error('Upload failed'); }
    }

    setForm(p => ({ ...p, documents: [...(p.documents || []), doc] }));
    setNewDoc({ description: '', start_date: '', end_date: '', attach_in_order: false });
    setFile(null);
    setShowAdd(false);
  };

  const removeDoc = (i) => {
    setForm(p => ({ ...p, documents: p.documents.filter((_, j) => j !== i) }));
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }); } catch { return d; }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4 mr-1" /> Attached Documents
        </Button>
        <span className="text-sm text-muted-foreground">Total Records: {(form.documents || []).length}</span>
      </div>

      {showAdd && (
        <div className="border rounded-lg p-4 mb-4 space-y-3 bg-muted/10">
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${file ? 'border-primary bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/50'}`}
            onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
            onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
            onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
          >
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <Upload className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{file.name}</span>
                <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                <Button variant="ghost" size="sm" onClick={() => setFile(null)}><X className="h-3 w-3" /></Button>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-1">Drag & drop file here</p>
                <p className="text-xs text-muted-foreground mb-2">— or —</p>
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>Select File</Button>
                <input type="file" ref={fileRef} onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input value={newDoc.description} onChange={e => setNewDoc(p => ({ ...p, description: e.target.value }))} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={newDoc.start_date} onChange={e => setNewDoc(p => ({ ...p, start_date: e.target.value }))} className="text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={newDoc.end_date} onChange={e => setNewDoc(p => ({ ...p, end_date: e.target.value }))} className="text-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm pt-5">
              <Switch checked={newDoc.attach_in_order} onCheckedChange={v => setNewDoc(p => ({ ...p, attach_in_order: v }))} />
              Attach in Order
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleUpload}>Upload</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowAdd(false); setFile(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {(form.documents || []).length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No documents attached.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>File Type</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Attach in Order</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(form.documents || []).map((d, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs text-primary underline cursor-pointer">{d.file_name}</TableCell>
                <TableCell className="text-xs">{d.description}</TableCell>
                <TableCell className="text-xs">{d.file_type || 'File'}</TableCell>
                <TableCell className="text-xs">{formatDate(d.start_date)}</TableCell>
                <TableCell className="text-xs">{formatDate(d.end_date)}</TableCell>
                <TableCell className="text-xs">{d.attach_in_order ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => removeDoc(i)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
